import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../constants";
import { apiFetch } from "../services/api.client";

// ── Types ──────────────────────────────────────────────────────────────────

interface ActiveImport {
  jobId: string;
  type: "csv" | "zip";
  label: string;          // e.g. "Importing 5,000 patients" or "Uploading prescriptions.zip"
  status: "running" | "pending_async" | "completed" | "failed";
  createdRows?: number;
  mergedRows?: number;
  errorRows?: number;
  skippedRows?: number;
  totalRows?: number;
  processedRows?: number;
  progressPct?: number;   // 0-100
  errorReport?: { row: number; reason: string }[] | null;
}

interface ImportProgressContextValue {
  /** Currently active background import (null = nothing running) */
  activeImport: ActiveImport | null;
  /** Start tracking a background import. Polling begins immediately. */
  startTracking: (jobId: string, type: "csv" | "zip", label: string) => void;
  /** Stop tracking (e.g. user dismissed the banner). */
  dismiss: () => void;
}

const ImportProgressContext = createContext<ImportProgressContextValue>({
  activeImport: null,
  startTracking: () => {},
  dismiss: () => {},
});

// ── localStorage persistence ──────────────────────────────────────────────

const LS_KEY = "kaagazy_active_import";

function saveToStorage(imp: { jobId: string; type: "csv" | "zip"; label: string }) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(imp)); } catch { /* quota */ }
}

function clearStorage() {
  try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
}

function loadFromStorage(): { jobId: string; type: "csv" | "zip"; label: string } | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.jobId) return parsed;
  } catch { /* corrupt */ }
  return null;
}

// ── Helper ─────────────────────────────────────────────────────────────────

// ── Provider ───────────────────────────────────────────────────────────────

export const ImportProgressProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeImport, setActiveImport] = useState<ActiveImport | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attemptsRef = useRef(0);
  const lastProgressRef = useRef<{ pct: number; at: number }>({ pct: -1, at: Date.now() });
  const resumedRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    attemptsRef.current = 0;
  }, []);

  const dismiss = useCallback(() => {
    stopPolling();
    setActiveImport(null);
    clearStorage();
  }, [stopPolling]);

  const startPolling = useCallback((jobId: string, type: "csv" | "zip", label: string) => {
    stopPolling();
    attemptsRef.current = 0;
    lastProgressRef.current = { pct: -1, at: Date.now() };

    setActiveImport({ jobId, type, label, status: "running" });
    saveToStorage({ jobId, type, label });

    const poll = async () => {
      attemptsRef.current++;
      // Timeout if progress hasn't changed for 5 minutes (stale detection).
      // This handles large imports (12k files) that run for hours while still
      // catching stuck jobs.
      const staleSecs = (Date.now() - lastProgressRef.current.at) / 1000;
      if (staleSecs > 300) {
        setActiveImport(prev => prev ? { ...prev, status: "failed" } : null);
        clearStorage();
        toast.error("Background import timed out — no progress for 5 minutes");
        return;
      }

      try {
        const resp = await apiFetch(`${API_BASE_URL}/settings/import/jobs/${jobId}/progress`);
        if (!resp.ok) {
          timerRef.current = setTimeout(poll, 5000);
          return;
        }
        const d = (await resp.json()).data;

        if (d.status === "completed" || d.status === "failed") {
          const skipped = d.skipped_rows ?? 0;
          setActiveImport(prev => prev ? {
            ...prev,
            status: d.status,
            createdRows: d.created_rows ?? 0,
            mergedRows: d.merged_rows ?? 0,
            errorRows: d.error_rows ?? 0,
            skippedRows: skipped,
            totalRows: d.total_rows ?? 0,
            progressPct: 100,
            errorReport: d.error_report ?? null,
          } : null);

          clearStorage();

          if (d.status === "completed") {
            const parts = type === "zip"
              ? [`${d.created_rows ?? 0} uploaded`]
              : [`${d.created_rows ?? 0} created`, `${d.merged_rows ?? 0} merged`];
            if ((d.error_rows ?? 0) > 0) parts.push(`${d.error_rows} failed`);
            if (type === "zip" && skipped > 0) parts.push(`${skipped} skipped`);
            toast.success(`Import complete: ${parts.join(", ")}`);
          } else {
            toast.error("Background import failed — check job history for details");
          }
          // Keep the result visible in the banner for 30s so the user sees it
          timerRef.current = setTimeout(() => setActiveImport(null), 30_000);
          return;
        }

        // Still running — update progress counters.
        // For pending_async (waiting for Lambda cold start / S3 trigger),
        // treat any valid response as "alive" to avoid premature timeout.
        const newPct = d.progress_pct ?? 0;
        const isPendingAsync = d.status === "pending_async";
        if (newPct !== lastProgressRef.current.pct || isPendingAsync) {
          lastProgressRef.current = { pct: newPct, at: Date.now() };
        }
        setActiveImport(prev => prev ? {
          ...prev,
          status: d.status === "pending_async" ? "running" : d.status,
          totalRows: d.total_rows ?? prev.totalRows,
          processedRows: d.processed_rows ?? prev.processedRows,
          progressPct: d.progress_pct ?? prev.progressPct,
          createdRows: d.created_rows ?? prev.createdRows,
          mergedRows: d.merged_rows ?? prev.mergedRows,
          errorRows: d.error_rows ?? prev.errorRows,
          skippedRows: d.skipped_rows ?? prev.skippedRows,
        } : null);
        timerRef.current = setTimeout(poll, 3000);
      } catch {
        timerRef.current = setTimeout(poll, 5000);
      }
    };

    timerRef.current = setTimeout(poll, 2000);
  }, [stopPolling]);

  // Resume polling on mount if there was an active import before reload
  useEffect(() => {
    if (resumedRef.current) return;
    resumedRef.current = true;

    const saved = loadFromStorage();
    if (saved) {
      startPolling(saved.jobId, saved.type, saved.label);
    }
  }, [startPolling]);

  return (
    <ImportProgressContext.Provider value={{ activeImport, startTracking: startPolling, dismiss }}>
      {children}
    </ImportProgressContext.Provider>
  );
};

export const useImportProgress = () => useContext(ImportProgressContext);
